import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, X, Send } from "lucide-react";

const PHONE_NUMBER = "+971541873527";
const WHATSAPP_LINK = `https://wa.me/971541873527?text=Hello%20Guru%20Maa,%20I%20would%20like%20to%20consult%20about%20my%20horoscope.%20Please%20guide%20me.`;

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showBubble, setShowBubble] = useState(true);

  // Entrance animation delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-hide bubble after interaction
  useEffect(() => {
    if (isOpen) {
      setShowBubble(false);
    }
  }, [isOpen]);

  const handleWhatsAppClick = () => {
    window.open(WHATSAPP_LINK, "_blank");
    setIsOpen(false);
  };

  const handleCallClick = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
    setIsOpen(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="floating-chat">
      <AnimatePresence>
        {/* Expanded Chat Interface */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute bottom-20 right-0 w-80 rounded-2xl overflow-hidden shadow-2xl"
            style={{
              background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
              border: "1px solid rgba(212, 175, 55, 0.2)",
              boxShadow:
                "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(212, 175, 55, 0.1)",
            }}
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B4941F] flex items-center justify-center">
                    <span className="text-xl">✨</span>
                  </div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-[#1E293B]" />
                </div>
                <div className="flex-1">
                  <h4 className="text-[#F8FAFC] font-semibold text-sm">
                    Guru Maa Pooja Sharma
                  </h4>
                  <p className="text-green-400 text-xs flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    Available Now
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-[#94A3B8] hover:text-white"
                  aria-label="Close chat"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-4 space-y-4">
              {/* Welcome Message */}
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B4941F] flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">✨</span>
                </div>
                <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 max-w-[85%]">
                  <p className="text-[#F8FAFC] text-sm">
                    Welcome! I am Guru Maa Pooja Sharma. How may I assist you on
                    your spiritual journey today?
                  </p>
                  <p className="text-[#94A3B8] text-xs mt-2">
                    Choose an option below to connect instantly.
                  </p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-2">
                <p className="text-[#94A3B8] text-xs uppercase tracking-wider px-1">
                  Quick Connect
                </p>

                {/* WhatsApp Option */}
                <button
                  onClick={handleWhatsAppClick}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 hover:bg-[#25D366]/20 transition-all group"
                  data-testid="chat-whatsapp-btn"
                >
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <MessageCircle size={20} className="text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-[#F8FAFC] font-medium text-sm">
                      Chat on WhatsApp
                    </p>
                    <p className="text-[#25D366] text-xs">Instant Response</p>
                  </div>
                  <Send
                    size={16}
                    className="text-[#25D366] group-hover:translate-x-1 transition-transform"
                  />
                </button>

                {/* Call Option */}
                <button
                  onClick={handleCallClick}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20 transition-all group"
                  data-testid="chat-call-btn"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B4941F] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Phone size={20} className="text-[#0F172A]" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-[#F8FAFC] font-medium text-sm">
                      Call Guru Maa
                    </p>
                    <p className="text-[#D4AF37] text-xs">+971541873527</p>
                  </div>
                  <Phone
                    size={16}
                    className="text-[#D4AF37] group-hover:rotate-12 transition-transform"
                  />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-white/5 bg-black/20">
              <p className="text-[#94A3B8] text-xs text-center">
                Available 9 AM - 9 PM IST • Trusted by 10,000+ Clients
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Bubble Message */}
      <AnimatePresence>
        {showBubble && !isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            className="absolute bottom-16 right-0 mb-2"
          >
            <div
              className="relative bg-white rounded-2xl rounded-br-sm px-4 py-3 shadow-lg max-w-[200px]"
              style={{ boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)" }}
            >
              <p className="text-[#0F172A] text-sm font-medium">
                ✨ Need guidance?
              </p>
              <p className="text-[#64748B] text-xs mt-1">
                Connect with Guru Maa
              </p>
              {/* Triangle pointer */}
              <div className="absolute -bottom-2 right-4 w-4 h-4 bg-white transform rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Toggle Button */}
      <motion.button
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative group"
        data-testid="floating-chat-toggle"
      >
        {/* Pulse rings */}
        {!isOpen && (
          <>
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
            <span
              className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-10"
              style={{ animationDelay: "0.5s" }}
            />
          </>
        )}

        {/* Button */}
        <div
          className={`relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOpen
              ? "bg-[#1E293B] border-2 border-white/20"
              : "bg-gradient-to-br from-[#25D366] to-[#128C7E] shadow-lg"
          }`}
          style={{
            boxShadow: isOpen
              ? "none"
              : "0 8px 30px rgba(37, 211, 102, 0.4), 0 0 20px rgba(37, 211, 102, 0.2)",
          }}
        >
          {isOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <MessageCircle size={28} className="text-white" />
          )}
        </div>

        {/* Hover glow effect */}
        <div
          className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            boxShadow: isOpen
              ? "0 0 30px rgba(255, 255, 255, 0.1)"
              : "0 0 40px rgba(37, 211, 102, 0.5)",
          }}
        />
      </motion.button>
    </div>
  );
};

export default FloatingChat;
